"use client";
import Link from "next/link";
import { Button } from "@/components/ui/button";

export default function NotFoundPage() {
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-200 via-blue-100 to-green-200 dark:from-blue-950 dark:via-zinc-900 dark:to-green-900 px-4 overflow-hidden">
      {/* Magical background sun/moon */}
      <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 z-0">
        <div className="w-60 h-60 rounded-full bg-yellow-100 dark:bg-blue-900 opacity-30 blur-2xl"></div>
      </div>
      {/* Floating magical food delivery elements */}
      <div className="absolute left-8 top-16 text-5xl opacity-60 animate-float1 select-none">
        🛵
      </div>
      <div className="absolute right-10 top-32 text-4xl opacity-50 animate-float2 select-none">
        🥗
      </div>
      <div
        className="absolute left-1/2 bottom-20 text-6xl opacity-40 animate-float3 select-none"
        style={{ transform: "translateX(-50%)" }}
      >
        🍎
      </div>
      <div className="absolute left-1/4 top-1/3 text-3xl opacity-70 animate-float4 select-none">
        🛒
      </div>
      <div className="absolute right-1/4 top-1/4 text-2xl opacity-70 animate-float5 select-none">
        🥖
      </div>
      <div className="absolute left-1/3 bottom-32 text-3xl opacity-60 animate-float6 select-none">
        🍕
      </div>
      {/* Soot sprites as lost groceries */}
      <div className="absolute left-16 bottom-24 text-2xl opacity-80 animate-soot1 select-none">
        🍅
      </div>
      <div className="absolute right-20 bottom-32 text-xl opacity-70 animate-soot2 select-none">
        🥦
      </div>
      {/* Mist/fog at the bottom */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white/80 via-white/40 to-transparent dark:from-zinc-900/80 dark:via-zinc-900/40 z-0 pointer-events-none"></div>
      <div className="flex flex-col items-center z-10 mt-10 mb-20">
        <div className="text-[7rem] mb-4 drop-shadow-lg animate-bounce-slow select-none">
          🧺
        </div>
        <h1 className="text-5xl font-ghibli font-bold mb-3 text-center text-green-700 dark:text-green-300 drop-shadow-lg">
          404 – Delivery Lost in the Wild!
        </h1>
        <p className="text-xl text-green-700 dark:text-green-200 mb-2 text-center max-w-xl italic drop-shadow-sm">
          &quot;Your groceries took a detour through the enchanted market.&quot;
        </p>
        <p className="text-lg text-zinc-700 dark:text-zinc-200 mb-10 text-center max-w-lg">
          Sorry, we couldn&apos;t find the page (or your order)! Let&apos;s get
          you back to the main menu.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-green-500 hover:bg-green-600 rounded-full text-lg font-semibold shadow-lg px-8 py-4"
        >
          <Link href="/">🍽️ Return to Home</Link>
        </Button>
      </div>
      <style jsx global>{`
        @font-face {
          font-family: "ghibli";
          src: local("Comic Sans MS"), local("Comic Sans"), local("Chalkboard"),
            sans-serif;
        }
        .font-ghibli {
          font-family: "ghibli", "Comic Sans MS", "Chalkboard", cursive,
            sans-serif;
          letter-spacing: 0.01em;
        }
        .animate-bounce-slow {
          animation: bounce 2.5s infinite;
        }
        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-16px);
          }
        }
        .animate-float1 {
          animation: float1 7s ease-in-out infinite;
        }
        .animate-float2 {
          animation: float2 9s ease-in-out infinite;
        }
        .animate-float3 {
          animation: float3 11s ease-in-out infinite;
        }
        .animate-float4 {
          animation: float4 8s ease-in-out infinite;
        }
        .animate-float5 {
          animation: float5 10s ease-in-out infinite;
        }
        .animate-float6 {
          animation: float6 12s ease-in-out infinite;
        }
        @keyframes float1 {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-24px);
          }
        }
        @keyframes float2 {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(18px);
          }
        }
        @keyframes float3 {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-30px);
          }
        }
        @keyframes float4 {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-12px);
          }
        }
        @keyframes float5 {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(10px);
          }
        }
        @keyframes float6 {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-16px);
          }
        }
        .animate-soot1 {
          animation: soot1 6s ease-in-out infinite;
        }
        .animate-soot2 {
          animation: soot2 8s ease-in-out infinite;
        }
        @keyframes soot1 {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-8px) scale(1.1);
          }
        }
        @keyframes soot2 {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(10px) scale(1.15);
          }
        }
      `}</style>
    </div>
  );
}
